import {
  Answer,
  GroupedQuestionField,
  GroupedQuestionnaireData,
  Question,
  QuestionnaireResponse,
  StandardQuestionnaireData,
} from '../types';

// Type guards to help differentiate between the two formats
export function isGroupedQuestionnaire(
  data: QuestionnaireResponse['data']
): data is GroupedQuestionnaireData {
  return data.hasGroups === true;
}

export function isStandardQuestionnaire(
  data: QuestionnaireResponse['data']
): data is StandardQuestionnaireData {
  return data.hasGroups === false;
}

// Helper function to determine field type based on response_type and validation rules
const determineFieldType = (
  type?: string,
  response_type?: string,
  validation_rules?: any
): Question['type'] => {
  if (type) return type as Question['type'];

  switch (response_type) {
    case 'Radio Button':
      return 'radio';
    case 'Text':
      return validation_rules?.data_type === 'number' ? 'number' : 'text';
    case 'Radio + Text':
      return 'radio-text';
    case 'Radio OR Text':
      return 'radio/text';
    default:
      return 'text';
  }
};

// Helper function to convert GroupedQuestionField to Question format
export const convertGroupedFieldsToQuestions = (
  fields: GroupedQuestionField[]
): Question[] => {
  return fields.map(field => {
    const baseQuestion: Question = {
      id: field.id,
      key: field.id,
      component: field.component,
      // q_number: field.q_number,
      type: determineFieldType(
        field.type,
        field.response_type,
        field.validation_rules
      ),
      defaultValue: field.default_value,
      selectedOptionId: field.selected_option_id || null,
      label: field.question,
      required: !!field?.required,
      attributes: field.attributes || {},
      colSpan: 2,
      helpText: (field as any).help_text || '',
      hasAttachment: field.has_attachment || false,
      uploadedFilenames: field.uploaded_filenames || [],
      dependency_list: field.dependency_list || [],
      excluded: field.excluded || false, // Preserve exclusion state
      exclusionReason: field.exclusionReason, // Preserve exclusion reason
      validation_rules: field.validation_rules, // Add validation rules
      attachments: field.attachments || [],
      hasSubQuestions: field.hasSubQuestions || false,
      enables_autofill: field.enables_autofill || false,
      autofill_targets: field.autofill_targets || [],
      tooltip_text: field.tooltip_text,
      options:
        field.options?.map(option => ({
          infotext: option.info_text || undefined,
          label: option.option_text || `Option ${option.id}`,
          value: option.id.toString(),
          id: option.id.toString(),
          has_attachment: option.has_attachment || false,
        })) || [],
    };

    // Add subquestions if they exist
    if (field.hasSubQuestions && field.subQuestions) {
      baseQuestion.subQuestions = field.subQuestions.map(subQ => ({
        id: subQ.id,
        key: subQ.id,
        isSubQuestion: true,
        // q_number: field.q_number,
        type: determineFieldType(
          undefined,
          subQ.response_type,
          subQ.validation_rules
        ),
        tooltip_text: subQ.tooltip_text,
        defaultValue: subQ.default_value,
        label: subQ.question,
        required: !!subQ?.required,
        colSpan: 2,
        hasAttachment: subQ.has_attachment || false,
        attributes: subQ.attributes || {},
        helpText: (subQ as any).help_text || '',
        uploadedFilenames: subQ.uploaded_filenames || [],
        dependency_list: subQ.dependency_list || [],
        excluded: subQ.excluded || false, // Preserve exclusion state
        exclusionReason: subQ.exclusionReason, // Preserve exclusion reason
        validation_rules: subQ.validation_rules, // Add validation rules
        attachments: subQ.attachments || [],
        enables_autofill: subQ.enables_autofill || false,
        autofill_targets: subQ.autofill_targets || [],
        options:
          subQ.options?.map(option => ({
            infotext: option.info_text || undefined,
            label: option.option_text || `Option ${option.id}`,
            value: option.id.toString(),
            id: option.id.toString(),
            has_attachment: option.has_attachment || false,
            triggers_autofill: option.triggers_autofill || false,
          })) || [],
      }));
    }

    return baseQuestion;
  });
};

//used to check if all required questions are answered and if all questions are answered
//only used to send isDraft flag from frontend to backend
export const isQuestionnaireComplete = (
  questionnaireData:
    | QuestionnaireResponse['data']
    | GroupedQuestionnaireData
    | null,
  answers: Record<string, Answer>
): boolean => {
  if (!questionnaireData) return false;

  console.log(
    'Checking completeness in questionnaireData ::',
    questionnaireData,
    answers
  );
  const checkFieldsComplete = (fields: Question[]): boolean => {
    return fields.every(question => {
      // Skip optional questions
      // if (!question.required || question.attributes?.readOnly) return true;
      //skip the questions which has readonly attribute and are excluded
      if (question.attributes?.readOnly || question.excluded) return true;
      console.log('Question inside isComplete ::', question);

      // Check if main question has answer
      const answerValue = answers[question.id]?.value;
      const hasAnswer =
        answerValue !== undefined &&
        answerValue !== null &&
        (answerValue.selectedOptionId !== null ||
          (typeof answerValue.typedValue === 'string' &&
            answerValue.typedValue.trim().length > 0));

      console.log('has answer::', hasAnswer);

      // Handle RadioGrid sub-questions
      if (
        question.type === 'custom' &&
        question.component === 'radio-grid' &&
        question.subQuestions
      ) {
        const subQuestionsComplete = question.subQuestions.every(
          subQuestion => {
            const subAnswer = answers[subQuestion.id];
            return subAnswer?.value !== undefined && subAnswer?.value !== null;
          }
        );
        return hasAnswer && subQuestionsComplete;
      }

      // Handle nested group fields
      if (question.type === 'group' && question.fields) {
        return hasAnswer && checkFieldsComplete(question.fields);
      }

      return hasAnswer;
    });
  };

  const checkGroupedFieldsComplete = (
    fields: GroupedQuestionField[]
  ): boolean => {
    console.log('Checking fields inside checkGroupedFieldsComplete ::', fields);
    return fields.every(field => {
      // Skip optional questions
      if (field.excluded) return true;

      // Check if main question has answer
      const hasAnswer =
        answers[field.id]?.value !== undefined &&
        answers[field.id]?.value !== null &&
        (answers[field.id]?.value.selectedOptionId !== null ||
          (typeof answers[field.id]?.value.typedValue === 'string' &&
            answers[field.id]?.value?.typedValue));

      // Handle subquestions
      if (field.hasSubQuestions && field.subQuestions) {
        const subQuestionsComplete = field.subQuestions.every(subQuestion => {
          const subAnswer = answers[subQuestion.id];
          return (
            subAnswer?.value !== undefined &&
            subAnswer?.value !== null &&
            (subAnswer?.value.selectedOptionId !== null ||
              (typeof subAnswer?.value.typedValue === 'string' &&
                subAnswer?.value?.typedValue))
          );
        });
        return hasAnswer && subQuestionsComplete;
      }

      return hasAnswer;
    });
  };

  // Handle different questionnaire types
  if (isGroupedQuestionnaire(questionnaireData)) {
    console.log('Is a grouped questionnaire');
    return questionnaireData.groups.every(group =>
      checkGroupedFieldsComplete(group.metadata.fields)
    );
  } else if (isStandardQuestionnaire(questionnaireData)) {
    console.log('Is a standard questionnaire');
    return checkFieldsComplete(questionnaireData.metadata.fields);
  }

  return false;
};

// Check if all required questions are answered (used only for standard questionnaire for now)
export const areRequiredQuestionsAnswered = (
  questionnaireData:
    | QuestionnaireResponse['data']
    | GroupedQuestionnaireData
    | null,
  answers: Record<string, Answer>
): boolean => {
  if (!questionnaireData) return false;
  console.log(
    'Checking required questions in questionnaireData ::',
    questionnaireData,
    answers
  );

  const checkFieldsComplete = (fields: Question[]): boolean => {
    return fields.every(question => {
      if (question.attributes?.readOnly || question.excluded) return true;
      console.log('Question inside  areRequiredQuestionsAnswered::', question);

      // Check if main question has answer
      const answerValue = answers[question.id]?.value;
      const hasAnswer =
        answerValue !== undefined &&
        answerValue !== null &&
        (answerValue.selectedOptionId !== null ||
          (typeof answerValue.typedValue === 'string' &&
            answerValue.typedValue.trim().length > 0));

      if (question.required && !hasAnswer) {
        return false;
      }
      return true;
    });
  };

  const checkGroupedFieldsComplete = (
    fields: GroupedQuestionField[]
  ): boolean => {
    return true; //do not check for grouped fields for now
    // console.log('Checking fields inside checkGroupedFieldsComplete ::', fields);
    // return fields.every(field => {
    //   // Skip optional questions
    //   if (field.excluded) return true;

    //   // Check if main question has answer
    //   const answerValue = answers[field.id]?.value;
    //   const hasAnswer =
    //     answerValue !== undefined &&
    //     answerValue !== null &&
    //     (answerValue.selectedOptionId !== null ||
    //       (answerValue.typedValue && answerValue.typedValue.trim().length > 0));

    //   // Handle subquestions
    //   if (field.hasSubQuestions && field.subQuestions) {
    //     const subQuestionsComplete = field.subQuestions.every(subQuestion => {
    //       const subAnswer = answers[subQuestion.id];
    //       return subAnswer?.value !== undefined && subAnswer?.value !== null;
    //     });
    //     return hasAnswer && subQuestionsComplete;
    //   }

    //   return hasAnswer;
    // });
  };

  // Handle different questionnaire types
  if (isGroupedQuestionnaire(questionnaireData)) {
    console.log('Is a grouped questionnaire');
    return questionnaireData.groups.every(group =>
      checkGroupedFieldsComplete(group.metadata.fields)
    );
  } else if (isStandardQuestionnaire(questionnaireData)) {
    return checkFieldsComplete(questionnaireData.metadata.fields);
  }

  return false;
};

// Validate required fields specifically for 'your-details' section
export const validateYourDetailsSection = (
  questionnaireData:
    | QuestionnaireResponse['data']
    | GroupedQuestionnaireData
    | null,
  answers: Record<string, Answer>
): {
  isValid: boolean;
  missingFields: { id: string; label: string; key?: string }[];
} => {
  const missingFields: { id: string; label: string; key?: string }[] = [];

  if (!questionnaireData) {
    return { isValid: false, missingFields };
  }

  console.log('Validating your-details section ::', questionnaireData, answers);

  const checkField = (question: Question) => {
    // Skip read-only or excluded fields
    if (
      question.attributes?.readOnly ||
      question.excluded ||
      !question.required
    )
      return true;
    // Check if answer exists and is valid
    const answerValue = answers[question.id]?.value;
    const hasAnswer =
      answerValue !== undefined &&
      answerValue !== null &&
      (answerValue.selectedOptionId !== null ||
        (typeof answerValue.typedValue === 'string' &&
          answerValue.typedValue.trim().length > 0));

    if (!hasAnswer) {
      missingFields.push({
        id: question.id,
        label: question.label,
        key: (question as any).key,
      });
      return false;
    }

    return true;
  };

  if (isStandardQuestionnaire(questionnaireData)) {
    questionnaireData.metadata.fields.forEach(field => checkField(field));
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

// //Validate hotel-details section
export const validateHotelDetailsSection = (
  questionnaireData:
    | QuestionnaireResponse['data']
    | GroupedQuestionnaireData
    | null,
  answers: Record<string, Answer>
): {
  isValid: boolean;
  missingFields: { id: string; label: string; key?: string }[];
} => {
  const missingFields: { id: string; label: string; key?: string }[] = [];

  if (!questionnaireData) {
    return { isValid: false, missingFields };
  }

  // console.log('Validating your-details section ::', questionnaireData, answers);

  const checkField = (question: Question) => {
    // Skip read-only or excluded fields
    if (question.attributes?.readOnly || !question.required) return true;
    // Check if answer exists and is valid
    const answerValue = answers[question.id]?.value;
    const hasAnswer =
      answerValue !== undefined &&
      answerValue !== null &&
      (answerValue.selectedOptionId !== null ||
        (typeof answerValue.typedValue === 'string' &&
          answerValue.typedValue.trim().length > 0));

    if (question.defaultValue && !answerValue) {
      return true;
    } else if (question.defaultValue && !hasAnswer) {
      missingFields.push({
        id: question.id,
        label: question.label,
        key: (question as any).key,
      });
      return false;
    }

    if (!hasAnswer) {
      missingFields.push({
        id: question.id,
        label: question.label,
        key: (question as any).key,
      });
      return false;
    }

    return true;
  };

  if (isStandardQuestionnaire(questionnaireData)) {
    questionnaireData.metadata.fields.forEach(field => checkField(field));
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

// Find all unanswered required questions
export const findUnansweredQuestions = (
  questionnaireData:
    | QuestionnaireResponse['data']
    | GroupedQuestionnaireData
    | null,
  answers: Record<string, Answer>
): string[] => {
  if (!questionnaireData) return [];
  console.log(
    'Finding unanswered questions in questionnaireData ::',
    questionnaireData,
    answers
  );

  const unansweredIds: string[] = [];

  const checkFields = (fields: Question[]) => {
    fields.forEach(question => {
      if (question.attributes?.readOnly || question.excluded) return;

      const answerValue = answers[question.id]?.value;
      const hasAnswer =
        answerValue !== undefined &&
        answerValue !== null &&
        (answerValue.selectedOptionId !== null ||
          (typeof answerValue.typedValue === 'string' &&
            answerValue.typedValue.trim().length > 0));

      if (!hasAnswer) {
        unansweredIds.push(String(question.id));
      }

      // Check subquestions
      if (question.subQuestions) {
        question.subQuestions.forEach(subQ => {
          if (subQ.excluded) return;
          const subHasAnswer =
            answers[subQ.id]?.value !== undefined &&
            answers[subQ.id]?.value !== null;
          if (!subHasAnswer) {
            unansweredIds.push(String(subQ.id));
          }
        });
      }
    });
  };

  const checkGroupedFields = (fields: GroupedQuestionField[]) => {
    fields.forEach(field => {
      if (field.excluded) return;

      const hasAnswer =
        answers[field.id]?.value !== undefined &&
        answers[field.id]?.value !== null &&
        (answers[field.id]?.value.selectedOptionId !== null ||
          (typeof answers[field.id]?.value.typedValue === 'string' &&
            answers[field.id]?.value?.typedValue));

      if (!hasAnswer) {
        unansweredIds.push(String(field.id));
      }

      // Check subquestions
      if (field.hasSubQuestions && field.subQuestions) {
        // Use for...of loop instead of forEach to allow break
        for (const subQ of field.subQuestions) {
          if (subQ.excluded) continue;
          const subHasAnswer =
            answers[subQ.id]?.value !== undefined &&
            answers[subQ.id]?.value !== null &&
            (answers[subQ.id]?.value.selectedOptionId !== null ||
              (typeof answers[subQ.id]?.value.typedValue === 'string' &&
                answers[subQ.id]?.value?.typedValue));

          if (
            field.type === 'custom' &&
            field.component === 'radio-grid' &&
            !subHasAnswer
          ) {
            console.log(
              'Unanswered subquestion in radio-grid ::',
              subQ.id,
              'so pushing main question id',
              field.id
            );
            //push the main question id
            unansweredIds.push(String(field.id));
            break;
          } else if (!subHasAnswer) {
            unansweredIds.push(String(subQ.id));
          }
        }
      }
    });
  };

  if (isGroupedQuestionnaire(questionnaireData)) {
    questionnaireData.groups.forEach(group =>
      checkGroupedFields(group.metadata.fields)
    );
  } else if (isStandardQuestionnaire(questionnaireData)) {
    checkFields(questionnaireData.metadata.fields);
  }

  return unansweredIds;
};
